export interface PaymentEventRow {
  id?: number
  parentPaymentId?: number | null
  amount?: number | string
  whTaxPrice?: number | string
}

export interface PaymentEventFee {
  incomeDocumentPaymentId?: number | string | null
  amount?: number | string
  reason?: number | string
}

export const paymentFeeSettlementAmount = (fees: PaymentEventFee[] = []) =>
  fees.reduce((total, fee) => {
    const amount = Number(fee.amount || 0)
    return total + (Number(fee.reason) === -1 ? amount : -amount)
  }, 0)

/**
 * Adjust only the fee difference on the first payment channel. This preserves
 * a partial amount entered by the user instead of resetting it to the full
 * document balance whenever a fee field changes.
 */
export const adjustPaymentsForFeeChange = <T extends PaymentEventRow>(
  payments: T[] = [],
  previousFees: PaymentEventFee[] = [],
  nextFees: PaymentEventFee[] = []
): T[] => {
  if (payments.length === 0) return payments

  // Cash adjustment is the inverse of settlement contribution:
  // reason -1 reduces cash received; reason 1 increases it.
  const feeDelta = paymentFeeSettlementAmount(previousFees) - paymentFeeSettlementAmount(nextFees)
  if (feeDelta === 0) return payments

  return payments.map((payment, index) => index === 0
    ? {
      ...payment,
      amount: Number((Number(payment.amount || 0) + feeDelta).toFixed(2)),
    }
    : payment)
}

interface PaymentDefaultAmountsInput {
  availableAmount: number
  defaultWhTax: number
  activePayments?: PaymentEventRow[]
  documentNetAmount?: number | string | null
  documentWhTax?: number | string | null
  isPaying?: boolean
}

/**
 * Resolve the new payment row's default without reusing an old document total.
 * In the receive-payment modal, `availableAmount` has already deducted paid
 * BLs and previous payments, so it must remain the source of truth.
 */
export const resolvePaymentDefaultAmounts = ({
  availableAmount,
  defaultWhTax,
  activePayments = [],
  documentNetAmount,
  documentWhTax,
  isPaying = false,
}: PaymentDefaultAmountsInput) => {
  const round2 = (value: number) => Number(value.toFixed(2))
  let whTaxPrice = round2(Number(defaultWhTax || 0))
  let paymentAmount = round2(Number(availableAmount || 0) - whTaxPrice)

  if (activePayments.length > 0) {
    const totalAmountReceived = activePayments.reduce(
      (total, payment) => total + Number(payment.amount || 0),
      0
    )
    const totalWhTaxReceived = activePayments.reduce(
      (total, payment) => total + Number(payment.whTaxPrice || 0),
      0
    )
    whTaxPrice = round2(Number(documentWhTax || 0) - totalWhTaxReceived)
    paymentAmount = isPaying
      ? round2(Number(availableAmount || 0) - whTaxPrice)
      : round2(Number(documentNetAmount || 0) - totalAmountReceived)
  }

  return {paymentAmount, whTaxPrice}
}

export const paymentEventRows = <T extends PaymentEventRow>(payments: T[] = [], parentPaymentId?: number | string | null) => {
  if (parentPaymentId === null || parentPaymentId === undefined) return []
  const parentId = Number(parentPaymentId)
  return payments.filter((payment) =>
    Number(payment.id) === parentId || Number(payment.parentPaymentId || 0) === parentId
  )
}

/** Fees belong to the payment event that owns their payment id. */
export const paymentEventFees = <T extends PaymentEventFee>(fees: T[] = [], payments: PaymentEventRow[] = []) => {
  const paymentIds = new Set(payments.map((payment) => Number(payment.id)).filter(Boolean))
  return fees.filter((fee) => paymentIds.has(Number(fee.incomeDocumentPaymentId)))
}

export const paymentEventTotal = (payments: PaymentEventRow[] = [], fees: PaymentEventFee[] = []) => {
  const paymentTotal = payments.reduce(
    (total, payment) => total + Number(payment.amount || 0) + Number(payment.whTaxPrice || 0),
    0
  )
  const feeSettlementTotal = paymentFeeSettlementAmount(fees)
  return Number((paymentTotal + feeSettlementTotal).toFixed(2))
}

export const paymentRemainingAmount = (
  documentAmount: number | string,
  payments: PaymentEventRow[] = [],
  fees: PaymentEventFee[] = []
) => Number((Number(documentAmount || 0) - paymentEventTotal(payments, fees)).toFixed(2))
